import { useCallback, useEffect, useMemo, useState } from 'react';
import apiGet, { apiDelete, apiPut } from '../../../Api';

const emptyGrouped = Object.freeze({});

const groupByCategory = (list) => {
  if (!Array.isArray(list)) return emptyGrouped;

  return list.reduce((acc, setting) => {
    const category = setting.category || 'Uncategorized';
    if (!acc[category]) acc[category] = [];
    acc[category].push(setting);
    return acc;
  }, {});
};

async function fetchSettings() {
  const response = await apiGet('/settings');
  const data = await response.json();
  return Array.isArray(data) ? data : [];
}

async function persistSetting(category, key, value) {
  await apiPut('/settings', JSON.stringify({ category, key, value }));
}

async function removeSetting(category, key) {
  await apiDelete(`/settings/${encodeURIComponent(category)}/${encodeURIComponent(key)}`);
}

export default function useSettings() {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mutationCount, setMutationCount] = useState(0);

  const mutating = mutationCount > 0;
  const beginMutation = useCallback(() => setMutationCount((count) => count + 1), []);
  const endMutation = useCallback(
    () => setMutationCount((count) => (count > 0 ? count - 1 : 0)),
    []
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchSettings();
      setSettings(data);
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const grouped = useMemo(() => groupByCategory(settings), [settings]);

  const updateSetting = useCallback(async (category, key, value) => {
    setSettings((prev) =>
      prev.map((s) => (s.category === category && s.key === key ? { ...s, value } : s))
    );

    try {
      await persistSetting(category, key, value);
      setError(null);
    } catch (err) {
      setError(err);
      await refresh();
    }
  }, [refresh]);

  const deleteSetting = useCallback(async (category, key) => {
    beginMutation();
    setSettings((prev) => prev.filter((s) => !(s.category === category && s.key === key)));
    try {
      await removeSetting(category, key);
      setError(null);
    } catch (err) {
      setError(err);
      await refresh();
    } finally {
      endMutation();
    }
  }, [beginMutation, endMutation, refresh]);

  const createSetting = useCallback(async (category, key, value) => {
    if (!category || !key) return false;

    beginMutation();
    try {
      await persistSetting(category, key, value);
      await refresh();
      setError(null);
      return true;
    } catch (err) {
      setError(err);
      return false;
    } finally {
      endMutation();
    }
  }, [beginMutation, endMutation, refresh]);

  return {
    settings,
    groupedSettings: grouped,
    loading,
    error,
    mutating,
    refresh,
    updateSetting,
    deleteSetting,
    createSetting,
  };
}
